/**
 * Template Management Models
 * Updated to align with backend API responses
 */

/**
 * Template status - extended to match backend TemplateStatus enum
 */
export type TemplateStatus = 'draft' | 'pending' | 'pending_approval' | 'approved' | 'rejected' | 'active' | 'inactive' | 'archived';

/**
 * Template category / document type
 */
export type TemplateCategory = 'invoice' | 'receipt' | 'purchase_order' | 'contract' | 'id_document' | 'bank_statement' | 'tax_form' | 'custom' | 'form' | 'other';

/**
 * Template visibility
 */
export type TemplateVisibility = 'public' | 'private' | 'organization';

/**
 * Field types supported in templates
 */
export type FieldType = 'string' | 'number' | 'date' | 'boolean' | 'array' | 'object' | 'email' | 'phone' | 'url' | 'currency';

/**
 * AI Provider options
 */
export type AIProvider = 'openai' | 'anthropic' | 'google';

/**
 * Template statistics
 */
export interface TemplateStats {
  total_templates: number;
  total_templates_change: number;
  pending_approvals: number;
  pending_approvals_change: number;
  total_downloads: number;
  total_downloads_change: number;
  active_templates: number;
  active_templates_change: number;
}

/**
 * Template field definition
 */
export interface TemplateField {
  name: string;
  label: string;
  type: FieldType;
  description?: string;
  required: boolean;
  validation_rules?: Record<string, any>;
  extraction_hints?: string;
  examples?: string[];
  default_value?: any;
  order: number;
}

/**
 * Complete template entity
 */
export interface Template {
  id: string;
  name: string;
  description?: string;
  category: TemplateCategory;
  document_type?: string;
  creator_organization_id?: string;
  creator_organization_name?: string;
  created_by?: string;
  status: TemplateStatus;
  visibility: TemplateVisibility;
  is_public: boolean;
  is_active: boolean;
  downloads: number;
  usage_count: number;
  created_at: string;
  updated_at: string;
  version: number;
  fields?: TemplateField[];
  field_count: number;
  instructions?: string;
  preferred_ai_provider?: AIProvider;
  avg_confidence_score?: number;
  success_rate?: number;
  parent_template_id?: string;
}

/**
 * Template version history entry
 */
export interface TemplateVersion {
  version: string;
  created_at: string;
  created_by: string;
  changes: string;
}

/**
 * Template details response
 */
export interface TemplateDetailsResponse {
  template: Template;
  versions: TemplateVersion[];
}

/**
 * Template filters
 */
export interface TemplateFilters {
  search?: string;
  status?: TemplateStatus | '';
  category?: TemplateCategory | '';
  visibility?: TemplateVisibility | '';
}

/**
 * Templates response
 */
export interface TemplatesResponse {
  templates: Template[];
  total: number;
  page: number;
  page_size: number;
}

/**
 * Request to create a new template
 */
export interface CreateTemplateRequest {
  name: string;
  description?: string;
  document_type: string;
  fields: TemplateField[];
  instructions?: string;
  preferred_ai_provider?: AIProvider;
  is_public?: boolean;
}

/**
 * Request to update an existing template
 */
export interface UpdateTemplateRequest {
  name?: string;
  description?: string;
  document_type?: string;
  fields?: TemplateField[];
  instructions?: string;
  preferred_ai_provider?: AIProvider;
  is_active?: boolean;
  is_public?: boolean;
  status?: TemplateStatus;
}

/**
 * Request to clone a template
 */
export interface CloneTemplateRequest {
  name: string;
  description?: string;
  selected_fields?: string[];  // Field names to include
}

/**
 * Template approval request
 */
export interface TemplateApprovalRequest {
  template_id: string;
  action: 'approve' | 'reject';
  reason?: string;
}

/**
 * Template approval response
 */
export interface TemplateApprovalResponse {
  success: boolean;
  message: string;
  template: Template;
}

/**
 * Approval request list item (Phase 1 - Approval Queue)
 */
export interface ApprovalRequestItem {
  request_id: string;
  template_id: string;
  template_name: string;
  document_type: string;
  request_type: string;
  status: 'pending' | 'approved' | 'rejected';
  requested_at: string;
  requested_by_name?: string;
  requested_by_email?: string;
  organization_id?: string;
  field_count?: number;
  is_public_template?: boolean;
  security_check_passed?: boolean;
}

/**
 * Paginated approval requests response
 */
export interface ApprovalQueueResponse {
  items: ApprovalRequestItem[];
  total: number;
  page: number;
  page_size: number;
  total_pages: number;
  has_next: boolean;
  has_prev: boolean;
}

/**
 * Approval statistics for dashboard
 */
export interface ApprovalStats {
  pending_count: number;
  approved_count: number;
  rejected_count: number;
  total_count: number;
  recent_pending: ApprovalRequestItem[];
}

/**
 * AI provider type for template configuration
 */
export type AIProviderType = 'openai' | 'anthropic' | 'google';

/**
 * Provider strategy for template processing
 */
export type ProviderStrategy = 'balanced' | 'cost_optimized' | 'quality_optimized' | 'custom';

/**
 * AI configuration for template approval
 */
export interface TemplateAIConfig {
  /** Preferred AI provider (openai, anthropic, google) or null for auto */
  preferred_ai_provider?: AIProviderType | null;
  /** Provider selection strategy */
  provider_strategy?: ProviderStrategy;
  /** Custom provider priority order when strategy is 'custom' */
  custom_provider_order?: AIProviderType[];
  /** Timeout for parsing in seconds (30-300) */
  parsing_timeout_seconds?: number;
  /** Maximum retry attempts if AI fails (1-10) */
  max_retries?: number;
  /** Delay between retries in seconds (1-60) */
  retry_delay_seconds?: number;
}

/**
 * Approval action (approve/reject) request
 */
export interface ApprovalActionRequest {
  notes?: string;  // For approval
  reason?: string; // For rejection (required, min 10 chars)
  ai_config?: TemplateAIConfig; // AI configuration during approval
}

/**
 * Template delete response
 */
export interface TemplateDeleteResponse {
  success: boolean;
  message: string;
  template_id?: string;
}

/**
 * JSON validation error
 */
export interface JsonValidationError {
  line?: number;
  column?: number;
  field?: string;
  message: string;
  severity: 'error' | 'warning';
}

/**
 * JSON validation response
 */
export interface JsonValidationResponse {
  valid: boolean;
  errors: JsonValidationError[];
  warnings: JsonValidationError[];
  field_count: number;
  parsed_template?: Record<string, any>;
}

/**
 * Template category metadata
 */
export interface TemplateCategoryMetadata {
  name: string;
  color: string;
  icon: string;
}

/**
 * Template status metadata
 */
export interface TemplateStatusMetadata {
  name: string;
  color: string;
}

/**
 * Field type metadata for UI display
 */
export interface FieldTypeInfo {
  value: FieldType;
  label: string;
  icon: string;
  description: string;
}

/**
 * Available field types with metadata
 */
export const FIELD_TYPES: FieldTypeInfo[] = [
  { value: 'string', label: 'Text', icon: 'text_fields', description: 'Single line or multi-line text' },
  { value: 'number', label: 'Number', icon: 'numbers', description: 'Numeric value (integer or decimal)' },
  { value: 'date', label: 'Date', icon: 'calendar_today', description: 'Date or datetime value' },
  { value: 'email', label: 'Email', icon: 'email', description: 'Email address' },
  { value: 'phone', label: 'Phone', icon: 'phone', description: 'Phone number' },
  { value: 'url', label: 'URL', icon: 'link', description: 'Web URL' },
  { value: 'currency', label: 'Currency', icon: 'attach_money', description: 'Monetary amount' },
  { value: 'boolean', label: 'Boolean', icon: 'toggle_on', description: 'True/false value' },
  { value: 'array', label: 'Array', icon: 'list', description: 'List of values' },
  { value: 'object', label: 'Object', icon: 'data_object', description: 'Nested object structure' }
];

/**
 * Category metadata map - expanded for all document types
 */
export const CATEGORY_METADATA: Record<string, TemplateCategoryMetadata> = {
  invoice: {
    name: 'Invoice',
    color: '#0066B3',
    icon: 'receipt'
  },
  receipt: {
    name: 'Receipt',
    color: '#27AE60',
    icon: 'receipt_long'
  },
  purchase_order: {
    name: 'Purchase Order',
    color: '#E67E22',
    icon: 'shopping_cart'
  },
  contract: {
    name: 'Contract',
    color: '#9B59B6',
    icon: 'description'
  },
  id_document: {
    name: 'ID Document',
    color: '#3498DB',
    icon: 'badge'
  },
  bank_statement: {
    name: 'Bank Statement',
    color: '#1ABC9C',
    icon: 'account_balance'
  },
  tax_form: {
    name: 'Tax Form',
    color: '#E74C3C',
    icon: 'calculate'
  },
  custom: {
    name: 'Custom',
    color: '#95A5A6',
    icon: 'note_add'
  },
  form: {
    name: 'Form',
    color: '#3498DB',
    icon: 'assignment'
  },
  other: {
    name: 'Other',
    color: '#7F8C8D',
    icon: 'insert_drive_file'
  }
};

/**
 * Status metadata map - expanded for all statuses
 */
export const STATUS_METADATA: Record<string, TemplateStatusMetadata> = {
  draft: {
    name: 'Draft',
    color: '#7F8C8D'
  },
  pending: {
    name: 'Pending',
    color: '#F39C12'
  },
  pending_approval: {
    name: 'Pending Approval',
    color: '#F39C12'
  },
  approved: {
    name: 'Approved',
    color: '#27AE60'
  },
  rejected: {
    name: 'Rejected',
    color: '#E74C3C'
  },
  active: {
    name: 'Active',
    color: '#2ECC71'
  },
  inactive: {
    name: 'Inactive',
    color: '#95A5A6'
  },
  archived: {
    name: 'Archived',
    color: '#BDC3C7'
  }
};

